// @flow

import React from 'react'
import styled from 'styled-components'
import ImageGallery from 'lib/ImageGallery'
import OptimizedImage from 'components/atoms/OptimizedImage'
import { imageURLWithTransform } from 'frontend_core/dist/helpers/format'
import { isMobile } from 'common/screen'
import { mediaMax } from 'components/styles/_media'
import { zIndexes } from 'frontend_core/dist/styles/variables'
import Colors from 'components/styles/colors'
import { imagePath } from 'frontend_core/dist/helpers/image'

const ModalCover = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 2001;
  visibility: hidden;
  backface-visibility: hidden;
  transform: translateX(-50%) translateY(-50%);
  ${props =>
    props.isShowingModal
      ? `
    visibility: visible;
    opacity: 1;
    transition: all 0.5s;
    overflow-x: hidden;
    overflow-y: auto;
  `
      : ''};
`

const ModalOverlay = styled.div`
  position: fixed;
  width: 100%;
  height: 100%;
  visibility: hidden;
  top: 0;
  left: 0;
  z-index: 2000;
  opacity: 0;
  background: rgba(0, 0, 0, 0.6);
  transition: all 0.3s;
  ${props =>
    props.isShowingModal
      ? `
    opacity: 1;
    visibility: visible;
  `
      : ''};
`

const ModalContent = styled.div`
  opacity: 0;
  transition: all 0.5s;
  position: absolute;
  width: 100%;
  left: 0;
  top: 8px
    ${props =>
      props.isShowingModal
        ? `
    opacity: 1;
  `
        : ''};
`

const ModalCloseOverlay = styled.div`
  width: 100%;
  height: 100%;
`

const SliderGalleryCancel = styled.p`
  font-size: 55px;
  text-align: right;
  width: 80%;
  margin: auto;
  padding: 0 0 10px 0;
  ${mediaMax.desktop`
    width: 100%;
    font-size: 50px;
    padding: 0 16px 10px;
  `};
`

const CloseButton = styled.a`
  cursor: pointer;
  color: #e9e9e9;
  display: block;
  &:hover {
    color: ${Colors.grayD};
  }
`

const SliderGalleryInner = styled.div``

const FlexSlider = styled.div`
  margin: 0;
  padding: 0;
  position: relative;
`

const Slider = FlexSlider.extend`
  width: 80%;
  margin: 0px auto;
  .image-gallery-slide {
    background: none;
  }
  ${mediaMax.desktop`
    width: 100%;
  `};
`

const Carousel = FlexSlider.extend`
  width: 90%;
  margin: 0 auto;
  ${mediaMax.desktop`
    display: none;
  `};
`

const Slides = styled.div`
  margin: 0;
  padding: 0;
  list-style: none;
  display: block;
  height: 1%;
  img {
    width: 100%;
    display: block;
  }
  &:after {
    content: '';
    display: block;
    clear: both;
    visibility: hidden;
    line-height: 0;
    height: 0;
  }
`

const SlideItem = styled.div`
  display: ${props => props.display};
`

const ThumbnailSlideItem = styled.li`
  border: 1px solid #000;
  padding-bottom: 0;
  margin-right: 5px;
  margin-bottom: 5px;
  width: 75px;
  height: 50px;
  float: left;
  overflow: hidden;
  cursor: pointer;
  &:hover {
    opacity: 0.8;
  }
  img {
    display: inline-block;
    vertical-align: top;
  }
`

const ActiveSlideItem = ThumbnailSlideItem.extend`
  border: 1px solid #fff;
`

const SlideItemCaption = styled.div`
  width: 100%;
  position: relative;
  margin-bottom: 6px;
  ${mediaMax.desktop`
    width: 96%;
    margin: 10px auto 0;
    padding: 0;
  `} p {
    color: ${Colors.white};
    position: absolute;
    bottom: 0;
    line-height: 1.5;
    text-shadow: 0 1px 1px #333;
    font-size: 14px;
    font-weight: bold;
    ${mediaMax.desktop`
      position: static;
      font-size: 12px;
    `};
  }
`

const SlideNavigation = styled.ul`
  margin: 0;
  padding: 0;
`

const SlideNavigationArrow = styled.a`
  text-decoration: none;
  display: block;
  width: 40px;
  height: 50px;
  margin: -20px 0 0;
  position: absolute;
  top: 46%;
  z-index: ${zIndexes.button};
  overflow: hidden;
  cursor: pointer;
  font-family: FontAwesome;
  ${mediaMax.desktop`
    height: 85vw;
    width: 80px;
    top: 15px;
    display: flex;
    align-items: center;
    padding-bottom: 15vw;
  `} &:before {
    font-size: 50px;
    display: inline-block;
    content: '\f053';
    opacity: 0.7;
    text-shadow: 0 0 2px rgba(0, 0, 0, 0.4);
    color: ${Colors.white};
    ${mediaMax.desktop`
      font-size: 20px;
    `};
  }
`

const SlideNavigationPrev = SlideNavigationArrow.extend`
  left: -70px;
  ${mediaMax.desktop`
    left: 10px;
  `};
`

const SlideNavigationNext = SlideNavigationArrow.extend`
  right: -70px;
  text-align: right;
  ${mediaMax.desktop`
    right: 10px;
    justify-content: flex-end;
  `} &:before {
    content: '\f054';
  }
`

type PropTypes = {
  thumbnails: Array<{
    roomName: string,
    url: string,
    description: string,
  }>,
  isShowingModal: number,
  currentDisplayingIndex: number,
  onDisplayAt: Function,
  onSlide: Function,
  onClickDismissModal: Function,
}

export default class HeadImageSlideShow extends React.Component<*> {
  static convertImageUrl(thumbnail: string, isThumbnail: boolean) {
    if (isThumbnail) {
      return imageURLWithTransform(thumbnail, { width: 219, height: 144 })
    }

    if (isMobile()) {
      return thumbnail
    }

    return imageURLWithTransform(thumbnail, { width: 1200, height: 670 })
  }

  constructor(props: PropTypes) {
    super(props)

    this.nextSlide = this.nextSlide.bind(this)
    this.prevSlide = this.prevSlide.bind(this)
    this.displayAtSlide = this.displayAtSlide.bind(this)
  }

  componentWillReceiveProps(nextProps: PropTypes) {
    if (nextProps.currentDisplayingIndex) {
      if (nextProps.isShowingModal) {
        this.imageGallery.slideToIndex(nextProps.currentDisplayingIndex)
      }
    }
  }

  imageGallery: ImageGallery
  nextSlide: Function
  prevSlide: Function
  displayAtSlide: Function

  nextSlide(e: Event) {
    e.preventDefault()
    this.displayAtSlide(e, this.imageGallery.getCurrentIndex() + 1)
  }

  prevSlide(e: Event) {
    e.preventDefault()
    this.displayAtSlide(e, this.imageGallery.getCurrentIndex() - 1)
  }

  displayAtSlide(e: Event, index: number) {
    this.imageGallery.slideToIndex(index)
    this.props.onDisplayAt(e, index)
  }

  props: PropTypes

  render() {
    const { thumbnails, currentDisplayingIndex, isShowingModal } = this.props

    const thumbs = []
    thumbnails.forEach(
      (
        thumbnail: { roomName: string, url: string, description: string },
        i,
      ) => {
        const ThumbnailItem =
          currentDisplayingIndex === i ? ActiveSlideItem : ThumbnailSlideItem
        thumbs.push(
          <ThumbnailItem
            key={`carousel${i}`}
            className={
              currentDisplayingIndex === i ? 'flex-active-slide' : null
            }
          >
            <a href="#" onClick={e => this.displayAtSlide(e, i)}>
              {thumbnail.url && (
                <OptimizedImage
                  src={HeadImageSlideShow.convertImageUrl(thumbnail.url, true)}
                  alt={i18n.t('page.room.thumbnailName', {
                    roomName: thumbnail.roomName,
                  })}
                />
              )}
            </a>
          </ThumbnailItem>,
        )
      },
    )

    return (
      <div>
        <ModalCover isShowingModal={isShowingModal}>
          <ModalCloseOverlay onClick={this.props.onClickDismissModal} />
          <ModalContent isShowingModal={isShowingModal}>
            <SliderGalleryCancel>
              <CloseButton onClick={this.props.onClickDismissModal}>
                ×
              </CloseButton>
            </SliderGalleryCancel>
            <SliderGalleryInner>
              <Slider>
                <ImageGallery
                  ref={ref => {
                    this.imageGallery = ref
                  }}
                  items={thumbnails}
                  infinite
                  lazyLoad
                  showPlayButton={false}
                  showFullscreenButton={false}
                  showThumbnails={false}
                  showNav={false}
                  useTranslate3D={false}
                  slideDuration={0}
                  onSlide={this.props.onSlide}
                  defaultImage={imagePath('noimage_search.png')}
                  renderItem={item => (
                    <div className="image-gallery-image">
                      <SlideItem>
                        {item.url && (
                          <OptimizedImage
                            src={HeadImageSlideShow.convertImageUrl(
                              item.url,
                              false,
                            )}
                            alt={i18n.t('page.room.photoName', {
                              roomName: item.roomName,
                            })}
                          />
                        )}
                      </SlideItem>
                      <div className="flex-caption">
                        <SlideItemCaption>
                          <p>{item.description}</p>
                        </SlideItemCaption>
                      </div>
                    </div>
                  )}
                />
                <SlideNavigation>
                  <SlideNavigationPrev
                    href="#"
                    tabIndex="-1"
                    onClick={this.prevSlide}
                  />
                  <SlideNavigationNext
                    href="#"
                    tabIndex="-1"
                    onClick={this.nextSlide}
                  />
                </SlideNavigation>
              </Slider>
              <Carousel>
                <Slides>{thumbs}</Slides>
              </Carousel>
            </SliderGalleryInner>
          </ModalContent>
        </ModalCover>
        <ModalOverlay isShowingModal={isShowingModal} />
      </div>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/organisms/room/CoverPhotoSlideShow/index.jsx